import { z } from 'zod';
import { aiCategory } from './topics.js';
import { translationDto } from './i18n.js';

export const articleCard = z.object({
  id: z.uuid(),
  title: z.string(),
  summary: z.string().nullable(),
  url: z.string(),
  imageUrl: z.string().nullable(),
  imageGenerated: z.boolean().default(false),
  tags: z.array(z.string()).default([]),
  author: z.string().nullable(),
  publishedAt: z.string(),
  contentStatus: z.enum(['pending', 'ok', 'failed', 'skipped']),
  feedTitle: z.string().nullable(),
  feedId: z.uuid(),
  faviconUrl: z.string().nullable(),
  readingTimeMin: z.number().int().nullable(),
  // AI category of the article; null when tagging is off or not yet run
  topic: aiCategory.nullable().default(null),
  // premium: translation into the requesting user's language, null when not applicable
  translation: translationDto.nullable().default(null),
});
export type ArticleCard = z.infer<typeof articleCard>;

export const clusterSource = z.object({
  articleId: z.uuid(),
  feedId: z.uuid(),
  feedTitle: z.string().nullable(),
  url: z.string(),
});
export type ClusterSource = z.infer<typeof clusterSource>;

export const clusterListItem = z.object({
  id: z.uuid(),
  representative: articleCard,
  sources: z.array(clusterSource),
  read: z.boolean(),
  saved: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ClusterListItem = z.infer<typeof clusterListItem>;

export const clustersQuery = z.object({
  filter: z.enum(['unread', 'all', 'saved']).default('unread'),
  category: z.string().optional(),
  tag: z.string().optional(),
  // AI category of the representative (NULL in db counts as 'jine')
  topic: aiCategory.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ClustersQuery = z.infer<typeof clustersQuery>;

export const clustersResponse = z.object({
  items: z.array(clusterListItem),
  nextCursor: z.string().nullable(),
  unreadCount: z.number().int(),
});
export type ClustersResponse = z.infer<typeof clustersResponse>;

// --- topics layout: sections per AI category ---
export const topicsQuery = z.object({
  filter: z.enum(['unread', 'all']).default('unread'),
  category: z.string().optional(),
});
export type TopicsQuery = z.infer<typeof topicsQuery>;

export const topicSection = z.object({
  topic: aiCategory,
  // total visible clusters in the topic (items is capped at TOPICS_SECTION_SIZE)
  count: z.number().int(),
  items: z.array(clusterListItem),
});
export type TopicSection = z.infer<typeof topicSection>;

export const topicsResponse = z.object({ sections: z.array(topicSection) });
export type TopicsResponse = z.infer<typeof topicsResponse>;

// junk-block cleaner result for the shown article: null = never cleaned, 0 = nothing removed, N = blocks removed
const aiCleanedBlocks = z.number().int().nullable().default(null);

export const clusterDetail = z.object({
  id: z.uuid(),
  representative: articleCard,
  contentHtml: z.string().nullable(),
  aiCleanedBlocks,
  sources: z.array(clusterSource),
  read: z.boolean(),
  saved: z.boolean(),
});
export type ClusterDetail = z.infer<typeof clusterDetail>;

export const articleContent = z.object({
  article: articleCard,
  contentHtml: z.string().nullable(),
  aiCleanedBlocks,
});
export type ArticleContent = z.infer<typeof articleContent>;

export const setReadBody = z.object({ read: z.boolean() });
export const setSavedBody = z.object({ saved: z.boolean() });
export const readAllBody = z.object({ before: z.string() });
